import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiEdit2, FiList, FiMoreVertical, FiRotateCcw, FiShare2, FiTrash2 } from 'react-icons/fi';
import { useStorageBox } from '../StorageBoxContext';
import { isPreviewableAudio } from '../api/storageBoxApi';
import StorageBoxItemMetaControls from './StorageBoxItemMetaControls';

const PANEL_WIDTH = 300;

export default function StorageBoxItemActionsMenu({
  item,
  onShare,
  onRename,
  onDelete,
  onQueueAudioPlayNext,
  className = '',
}) {
  const { browseMode, inLockVault, writable, readOnly, restoreBinItem, restoreBusyId } = useStorageBox();
  const inBin = browseMode === 'bin';
  const canManage = Boolean(item?.canManage) || (inLockVault && writable);
  const canAct = !readOnly && (canManage || (inBin && writable));
  const [open, setOpen] = useState(false);
  const [panelPos, setPanelPos] = useState(null);
  const rootRef = useRef(null);
  const panelRef = useRef(null);

  const updatePosition = () => {
    const el = rootRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const left = Math.min(Math.max(8, r.right - PANEL_WIDTH), window.innerWidth - PANEL_WIDTH - 8);
    setPanelPos({ top: r.bottom + 4, left });
  };

  useLayoutEffect(() => {
    if (!open) {
      setPanelPos(null);
      return;
    }
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      const t = e.target;
      if (rootRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!canAct) return null;

  const isAudioFile =
    item?.type === 'file' && isPreviewableAudio(item.mimeType, item.name);

  const restoreExpired = item?.daysUntilPurge != null && item.daysUntilPurge <= 0;
  const restoreBusy = restoreBusyId === item?.itemId;

  const panel =
    open && panelPos
      ? createPortal(
          <div
            ref={panelRef}
            className="sb-item-menu__panel sb-item-menu__panel--portal sb-item-menu__panel--meta"
            role="menu"
            style={{ top: panelPos.top, left: panelPos.left, width: PANEL_WIDTH }}
          >
            {inBin ? (
              <button
                type="button"
                role="menuitem"
                disabled={restoreExpired || restoreBusy}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  restoreBinItem(item);
                }}
              >
                <FiRotateCcw aria-hidden /> {restoreBusy ? 'Restoring…' : 'Restore'}
              </button>
            ) : (
              <>
                <div className="sb-item-menu__meta" onClick={(e) => e.stopPropagation()}>
                  <StorageBoxItemMetaControls item={item} compact />
                </div>
                {isAudioFile && onQueueAudioPlayNext ? (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpen(false);
                      onQueueAudioPlayNext(item);
                    }}
                  >
                    <FiList aria-hidden /> Play next in queue
                  </button>
                ) : null}
                <button
                  type="button"
                  role="menuitem"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen(false);
                    onRename?.(item);
                  }}
                >
                  <FiEdit2 aria-hidden /> Rename
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen(false);
                    onShare?.(item);
                  }}
                >
                  <FiShare2 aria-hidden /> {inLockVault ? 'Secure share' : 'Share'}
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="sb-item-menu__danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    setOpen(false);
                    onDelete?.(item);
                  }}
                >
                  <FiTrash2 aria-hidden /> Delete
                </button>
              </>
            )}
          </div>,
          document.body
        )
      : null;

  return (
    <div className={`sb-item-menu ${className}`.trim()} ref={rootRef}>
      <button
        type="button"
        className="sb-item-menu__trigger"
        aria-label={`Actions for ${item.name}`}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        <FiMoreVertical />
      </button>
      {panel}
    </div>
  );
}
